// Word-level diff of two short texts (comment versions), no library: longest
// common subsequence over word/space tokens. Returns runs of
// { type: "same" | "add" | "del", text }. Very long texts skip the diff (the
// table would be large) and come back as one del + one add run.

const MAX_CELLS = 1500000;

const tokens = (s = "") => s.split(/(\s+)/).filter(t => t !== "");

export const diffWords = (before = "", after = "") => {
  const ta = tokens(before);
  const tb = tokens(after);
  // Unchanged start and end are copied as-is, so a small edit in a long comment
  // only diffs the part around it.
  let start = 0;
  while (start < ta.length && start < tb.length && ta[start] === tb[start]) start++;
  let end = 0;
  while (end < ta.length - start && end < tb.length - start && ta[ta.length - 1 - end] === tb[tb.length - 1 - end]) end++;
  const head = ta.slice(0, start).join("");
  const tail = end ? ta.slice(ta.length - end).join("") : "";
  const a = ta.slice(start, ta.length - end);
  const b = tb.slice(start, tb.length - end);
  const wrap = (middle) => [
    ...(head ? [{ type: "same", text: head }] : []),
    ...middle,
    ...(tail ? [{ type: "same", text: tail }] : []),
  ];
  if (a.length * b.length > MAX_CELLS) {
    return wrap([...(a.length ? [{ type: "del", text: a.join("") }] : []), ...(b.length ? [{ type: "add", text: b.join("") }] : [])]);
  }
  // lcs[i][j] = length of the LCS of a[i:] and b[j:]
  const lcs = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const runs = [];
  const push = (type, text) => {
    const last = runs[runs.length - 1];
    if (last && last.type === type) last.text += text;
    else runs.push({ type, text });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++; }
    else if (lcs[i + 1][j] >= lcs[i][j + 1]) { push("del", a[i]); i++; }
    else { push("add", b[j]); j++; }
  }
  while (i < a.length) push("del", a[i++]);
  while (j < b.length) push("add", b[j++]);
  return wrap(runs);
};

export const hasChanges = (runs) => runs.some(r => r.type !== "same" && r.text.trim() !== "");
